import { useCallback, useEffect, useMemo, useState } from "react";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_Row,
  MRT_RowSelectionState,
  MRT_TableInstance,
  MRT_TableOptions,
  useMaterialReactTable
} from "material-react-table";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Box, Button, TextField, Tooltip, Typography } from "@mui/material";
import { ViewColumn } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { Colleague } from "../../Connections/types/Colleague.type";
import { commonLocalization } from "../../../functions/tableLocalization";
import BookingSvgDialog from "../../../components/Svg/svg-dialog.component";
import { ListTopToolbar } from "../../../components/Common/ListTablePartial/List-top-toolbar.partial";
import { ListRowActions } from "../../../components/Common/ListTablePartial/List-row-actions.partial";
import TableDeleteConfirmDialog from "../../../components/Common/TableDeletionConfirmDialog/TableDeletionConfirmDialog.component";
import { filterMRTEndDateProps } from "../../../features/Approval/booking-approval-hook.functions";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import {
  VisitorBookingsColumnToggleId,
  VisitorBookingsTableColumnToggles
} from "./visitor-bookings-table-ColumnToggles.partial";
import {
  isConferenceVisitorRegistrationRow,
  isVisitorAdminScheduleFieldEditable,
  isVisitorAdminTableRowEditable,
  mapRowsForTable,
  visitorBookingConsentColumnAccessorValue,
  visitorBookingHostDisplay,
  visitorBookingLocationDisplay,
  visitorBookingVisitorCount,
  type VisitorBookingEditDraft,
  VisitorBookingTableRow
} from "./visitor-bookings-table.partial";
import { buildVisitorAdminBulkRejectItems } from "./visitor-reception-api.logic";
import {
  renderVisitorBookingsActionsCell,
  renderVisitorBookingsConsentCell,
  renderVisitorBookingsConsentLinkCell,
  renderVisitorBookingsDateTimeEdit,
  renderVisitorBookingsHostCell,
  renderVisitorBookingsHostEdit,
  renderVisitorBookingsLocationEdit,
  renderVisitorBookingsPlaceCell,
  renderVisitorBookingsRequestedDateCell,
  renderVisitorBookingsVisitorCountCell
} from "./visitor-bookings-table-render.partial";
import { VisitorBookingsSlidePanelEditRow } from "./visitor-bookings-slide-panel-edit.partial";
import { getVisitorBookingVisitActionSnackbarKeys } from "./visitor-bookings-visit-action-snackbar.logic";
import { useRemotePatchActiveBookingRequests } from "../../../hooks/Remote/BookingRequests/useRemotePatchActiveBookingRequests";
import { useRequestAdminLocationAccess } from "../../../hooks/Remote/Admin/useRequestAdminLocationAccess";
import { canDeleteVisitorBookingRow } from "./visitor-bookings-registration-actions.logic";
import {
  buildBookingApprovalRejectPayload,
  canManageRequestedVisitorBooking,
  partitionRowsForVisitorReject,
  resolveVisitorBookingLocationInventoryId
} from "./visitor-bookings-request-admin.logic";
import { VisitorBookingsMode } from "../../../hooks/Remote/Schedule/visitor-booking-schedules.api";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { calculateMaxEndDate } from "../../../components/BookingForm/utils/booking.utils";
import { toIsoString } from "../../../components/FleetManagement/EventDetailDialog/functions/fleetEventSort.function";
import { validateStartEndForRowLogic } from "../../../components/FleetManagement/EventDetailDialog/functions/validateStartEndForRow.function";
import { useRemoteFetchAdminLocations } from "../../../hooks/Remote/Location/useRemoteFetchAdminLocations";
import { buildPatchConferenceVisitorRegistrationPayload } from "./functions/buildPatchConferenceVisitorRegistrationPayload.function";
import { buildPatchVisitOnlyBookingPayload } from "./functions/buildPatchVisitOnlyBookingPayload.function";
import { postVisitorAdminBulkRejectWithFallback } from "../../../hooks/Remote/Schedule/visitor-admin-bulk-reject.api";
import { useRemoteVisitOnlyVisitActions } from "../../../hooks/Remote/Schedule/useRemoteVisitOnlyVisitActions";
import { useRemoteResendVisitorConsentLink } from "../../../hooks/Remote/VisitConsent/useRemoteResendVisitorConsentLink";
import { VisitorAdminNewBookingDialog } from "./visitor-admin-new-booking-dialog.component";
import { withSeeditMrtInitialState } from "../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../themes/use-seedit-mrt-table-options";

type RowValidationErrors = {
  start?: string;
  end?: string;
};

/** Data column ids in default order (after MRT display columns in active mode). */
const VISITOR_BOOKINGS_DATA_COLUMN_IDS = [
  "externalName",
  "externalCompany",
  "externalEmail",
  "externalContacts",
  "start",
  "end",
  "host",
  "externalVisitReason",
  "visitorCount",
  "location",
  "consent",
  "consentLink",
  "place"
] as const;

const VISITOR_BOOKINGS_DATA_COLUMN_IDS_ARCHIVE = VISITOR_BOOKINGS_DATA_COLUMN_IDS.filter(
  columnId => columnId !== "consentLink"
);

/** Defaults for optional columns (fleet-style: some hidden until toggled). */
const defaultVisitorColumnVisibility: Record<VisitorBookingsColumnToggleId, boolean> = {
  visitorCount: false,
  externalCompany: false,
  externalEmail: true,
  externalContacts: false,
  externalVisitReason: true,
  location: true
};

type P = {
  mode: VisitorBookingsMode;
  rows: BookingScheduleInterface[];
  colleagues: Colleague[];
  isLoading: boolean;
  onRefetch: () => void;
};

export function VisitorBookingsTable({ mode, rows, colleagues, isLoading, onRefetch }: P) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { dateFormat, timeFormat } = useTimeFormat();
  const localeFullFormat = `${dateFormat} ${timeFormat}`;

  const {
    settings: { timezone },
    userInformation,
    userRoles
  } = useSelector((state: RootState) => state.login);
  const { company } = userInformation;

  const metaData = company?.meta;

  const isEditable = mode === "active";
  const [visitorAdminNewBookingOpen, setVisitorAdminNewBookingOpen] = useState(false);
  const [consentLinkSentRowKeys, setConsentLinkSentRowKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [sendingConsentLinkRowKey, setSendingConsentLinkRowKey] = useState<string | null>(null);
  const resendConsentLink = useRemoteResendVisitorConsentLink();
  const canManageConsentLink = userRoles.isVisitorAdmin || userRoles.isTenantAdmin;

  const markConsentLinkSent = useCallback((rowKey: string) => {
    setConsentLinkSentRowKeys(prev => {
      const next = new Set(prev);
      next.add(rowKey);
      return next;
    });
  }, []);

  const handleSendConsentLink = useCallback(
    (target: Parameters<typeof resendConsentLink.mutate>[0], rowKey: string) => {
      setSendingConsentLinkRowKey(rowKey);
      resendConsentLink.mutate(target, {
        onSuccess: () => {
          markConsentLinkSent(rowKey);
        },
        onSettled: () => {
          setSendingConsentLinkRowKey(current => (current === rowKey ? null : current));
        }
      });
    },
    [markConsentLinkSent, resendConsentLink]
  );

  const visitorAdminBooker = useMemo(
    () => ({
      sub: userInformation.sub,
      firstName: userInformation.firstName,
      surname: userInformation.surname,
      email: userInformation.email
    }),
    [userInformation.sub, userInformation.firstName, userInformation.surname, userInformation.email]
  );

  const handleAddVisitorBooking = useCallback(() => {
    setVisitorAdminNewBookingOpen(true);
  }, []);

  const { data: locations } = useRemoteFetchAdminLocations({ mode: "admin" });
  const { patchVisitOnly, patchConference } = useRemoteVisitOnlyVisitActions();
  const { mutateAsync: patchBookingRequestStatus } = useRemotePatchActiveBookingRequests();

  const [tableData, setTableData] = useState<VisitorBookingTableRow[]>(() => mapRowsForTable(rows));
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletionSelected, setDeletionSelected] = useState<VisitorBookingTableRow[] | null>(null);
  const [bulkRejectReason, setBulkRejectReason] = useState("");
  const [deleteStatus, setDeleteStatus] = useState<"error" | "loading" | "success" | "idle">(
    "idle"
  );

  const requestedRowLocationIds = useMemo(
    () =>
      tableData
        .filter(row => row.status === "REQUESTED")
        .map(row => resolveVisitorBookingLocationInventoryId(row, locations))
        .filter((id): id is number => id != null && id > 0),
    [tableData, locations]
  );

  const { canManageRequestedAtLocation } = useRequestAdminLocationAccess({
    locationInventoryIds: requestedRowLocationIds,
    userId: userInformation.sub,
    isRequestAdminRealm: userRoles.isRequestAdmin
  });

  const canManageRequestedForRow = useCallback(
    (visit: BookingScheduleInterface) =>
      canManageRequestedVisitorBooking(visit, canManageRequestedAtLocation, locations),
    [canManageRequestedAtLocation, locations]
  );

  const canDeleteRow = useCallback(
    (visit: BookingScheduleInterface) =>
      canDeleteVisitorBookingRow(visit, {
        canManageRequestedBookings: canManageRequestedForRow(visit)
      }),
    [canManageRequestedForRow]
  );

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => ({
    ...defaultVisitorColumnVisibility
  }));
  const [showColumnTogglesPanel, setShowColumnTogglesPanel] = useState(false);
  const [showPlaceDialogState, setShowPlaceDialogState] = useState<{
    bookingId: number;
    bookingType: BookingType;
    highlight?: number;
  } | null>(null);

  const [validationErrors, setValidationErrors] = useState<Record<string, RowValidationErrors>>({});
  const [draftByRowId, setDraftByRowId] = useState<Record<string, Partial<VisitorBookingTableRow>>>(
    {}
  );

  const setDraftForRow = useCallback((rowId: string, patch: Partial<VisitorBookingTableRow>) => {
    setDraftByRowId(prev => ({ ...prev, [rowId]: { ...(prev[rowId] ?? {}), ...patch } }));
  }, []);

  const clearDraftForRow = useCallback((rowId: string) => {
    setDraftByRowId(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  }, []);

  const clearValidationErrorForField = useCallback(
    (rowId: string, field: keyof RowValidationErrors) => {
      setValidationErrors(prev => {
        const next = { ...prev };
        const rowErr = next[rowId];
        if (rowErr) {
          const r = { ...rowErr };
          delete r[field];
          if (Object.keys(r).length > 0) {
            next[rowId] = r;
          } else {
            delete next[rowId];
          }
        }
        return next;
      });
    },
    []
  );

  const validateStartEndForRow = useCallback(
    (rowId: string, startIso: string | undefined, endIso: string | undefined): boolean => {
      const result = validateStartEndForRowLogic(startIso, endIso, {
        startRequired: t("_fleetEventDetail_startDateRequired"),
        endRequired: t("_fleetEventDetail_endDateRequired")
      });

      if (!result.valid) {
        setValidationErrors(prev => ({
          ...prev,
          [rowId]: { ...prev[rowId], [result.field]: result.message }
        }));
        return false;
      }

      setValidationErrors(prev => {
        const next = { ...prev };
        const rowErr = next[rowId];
        if (rowErr) {
          const r = { ...rowErr };
          delete r.start;
          delete r.end;
          if (Object.keys(r).length > 0) {
            next[rowId] = r;
          } else {
            delete next[rowId];
          }
        }
        return next;
      });
      return true;
    },
    [t]
  );

  const maxDateForVisitor = useMemo(() => {
    if (!metaData) {
      return undefined;
    }
    return DateTime.fromISO(
      calculateMaxEndDate({
        metaData,
        bookingMode: BookingType.VISITOR
      })
    ).toJSDate();
  }, [metaData]);

  const handleColumnToggle = (id: VisitorBookingsColumnToggleId) => {
    setColumnVisibility(prev => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    setTableData(mapRowsForTable(rows));
  }, [rows]);

  const onEditingRowSave: MRT_TableOptions<VisitorBookingTableRow>["onEditingRowSave"] = async ({
    row,
    values,
    exitEditingMode
  }) => {
    const draft = draftByRowId[row.id] ?? {};
    const merged = { ...row.original, ...values, ...draft } as VisitorBookingTableRow;
    const isConference = isConferenceVisitorRegistrationRow(row.original);
    const startIso = toIsoString(merged.start) ?? merged.start;
    const endIso = toIsoString(merged.end) ?? merged.end;

    if (!isConference && !validateStartEndForRow(row.id, startIso, endIso)) {
      return;
    }

    const editDraft: VisitorBookingEditDraft = {
      externalName: merged.externalName,
      externalCompany: merged.externalCompany,
      externalEmail: merged.externalEmail,
      externalContacts: merged.externalContacts,
      externalVisitReason: merged.externalVisitReason,
      userBookedBy: merged.userBookedBy,
      locationName: merged.locationName,
      start: startIso,
      end: endIso
    };

    const bookingTimezone = row.original.timezone ?? timezone;
    const body = isConference
      ? buildPatchConferenceVisitorRegistrationPayload({
          row: row.original,
          draft: editDraft,
          locations
        })
      : buildPatchVisitOnlyBookingPayload({
          row: row.original,
          draft: editDraft,
          locations,
          timezone: bookingTimezone
        });

    if (!body) {
      enqueueSnackbar(t("_visitorBookings_editNothingToSave"), { variant: "info" });
      return;
    }

    try {
      if (isConference) {
        const visitorRegistrationId = row.original.visitorRegistrationId ?? row.original.id;
        await patchConference.mutateAsync({
          visitorRegistrationId,
          body
        });
      } else {
        await patchVisitOnly.mutateAsync({
          visitId: row.original.id,
          body
        });
      }
      clearDraftForRow(row.id);
      exitEditingMode?.();
      onRefetch();
      enqueueSnackbar(t("_visitorBookings_editSavedSuccess"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("_visitorBookings_editFailed"), { variant: "error" });
    }
  };

  /** Active: Actions → Select → registration → Expand → data columns. Archive: data only. */
  const visitorBookingsDefaultColumnOrder = useMemo(() => {
    if (!isEditable) {
      return [...VISITOR_BOOKINGS_DATA_COLUMN_IDS_ARCHIVE];
    }
    return [
      "mrt-row-actions",
      "mrt-row-select",
      "registrationActions",
      "mrt-row-expand",
      ...VISITOR_BOOKINGS_DATA_COLUMN_IDS
    ];
  }, [isEditable]);

  /** Order: registration actions (active) → editable fields → read-only columns. */
  const columns = useMemo<MRT_ColumnDef<VisitorBookingTableRow>[]>(
    () => [
      ...(mode === "archive"
        ? []
        : [
            {
              id: "registrationActions",
              /** Blank: MRT `enableRowActions` already adds an "Actions" column header. */
              header: "",
              enableEditing: false,
              enableHiding: false,
              enableColumnFilter: false,
              enableSorting: false,
              size: 150,
              Cell: ({ row }: { row: MRT_Row<VisitorBookingTableRow> }) =>
                renderVisitorBookingsActionsCell({
                  row: row as unknown as MRT_Row<BookingScheduleInterface>,
                  onDone: onRefetch,
                  canManageRequestedAtLocation,
                  locations
                })
            }
          ]),
      {
        accessorKey: "externalName",
        header: t("_visitorBookings_colVisitorName"),
        enableEditing: row =>
          isEditable && isVisitorAdminScheduleFieldEditable(row.original, "guestName")
      },
      {
        accessorKey: "externalCompany",
        header: t("_visitorBookings_colCompany"),
        enableEditing: row =>
          isEditable && isVisitorAdminScheduleFieldEditable(row.original, "guestOther")
      },
      {
        accessorKey: "externalEmail",
        header: t("_visitorBookings_colEmail"),
        enableEditing: row =>
          isEditable && isVisitorAdminScheduleFieldEditable(row.original, "guestOther")
      },
      {
        accessorKey: "externalContacts",
        header: t("_visitorBookings_colContact"),
        enableEditing: row =>
          isEditable && isVisitorAdminScheduleFieldEditable(row.original, "guestOther")
      },
      {
        accessorKey: "start",
        accessorFn: row => new Date(row.start ?? ""),
        header: t("Start Date & Time"),
        Cell: ({ cell, row }) =>
          renderVisitorBookingsRequestedDateCell({ row, cell, localeFullFormat }),
        Edit: ({ row }) =>
          renderVisitorBookingsDateTimeEdit({
            row,
            field: "start",
            draft: draftByRowId[row.id],
            timezone,
            maxDate: maxDateForVisitor,
            validationError: validationErrors[row.id]?.start,
            onClearValidation: () => clearValidationErrorForField(row.id, "start"),
            onDraftChange: patch => setDraftForRow(row.id, patch)
          }),
        enableEditing: row =>
          isEditable && isVisitorAdminScheduleFieldEditable(row.original, "start"),
        muiFilterDatePickerProps: {
          format: dateFormat.toUpperCase()
        },
        filterFn: "greaterThan",
        filterVariant: "date",
        sortingFn: "datetime",
        size: 240
      },
      {
        accessorKey: "end",
        accessorFn: row => new Date(row.end ?? ""),
        header: t("End Date & Time"),
        sortingFn: "datetime",
        filterVariant: "date",
        filterFn: "lessThan",
        Cell: ({ cell, row }) =>
          renderVisitorBookingsRequestedDateCell({ row, cell, localeFullFormat }),
        Edit: ({ row }) =>
          renderVisitorBookingsDateTimeEdit({
            row,
            field: "end",
            draft: draftByRowId[row.id],
            timezone,
            maxDate: maxDateForVisitor,
            validationError: validationErrors[row.id]?.end,
            onClearValidation: () => clearValidationErrorForField(row.id, "end"),
            onDraftChange: patch => setDraftForRow(row.id, patch)
          }),
        enableEditing: row =>
          isEditable && isVisitorAdminScheduleFieldEditable(row.original, "end"),
        muiFilterDatePickerProps: ({ column }) => filterMRTEndDateProps(column as never, dateFormat)
      },
      {
        id: "host",
        header: t("_visitorBookings_colHost"),
        enableEditing: row =>
          isEditable && isVisitorAdminScheduleFieldEditable(row.original, "host"),
        accessorFn: row => visitorBookingHostDisplay(row, colleagues),
        Cell: ({ row }) => renderVisitorBookingsHostCell({ row, colleagues }),
        Edit: ({ row }) => renderVisitorBookingsHostEdit({ row, colleagues })
      },
      {
        accessorKey: "externalVisitReason",
        header: t("_visitorBookings_colVisitReason"),
        enableEditing: row =>
          isEditable && isVisitorAdminScheduleFieldEditable(row.original, "visitReason")
      },
      {
        id: "visitorCount",
        header: t("_visitorBookings_colVisitorCount"),
        enableEditing: false,
        accessorFn: row => visitorBookingVisitorCount(row),
        Cell: ({ row }) => renderVisitorBookingsVisitorCountCell({ row })
      },
      {
        id: "location",
        header: t("_visitorBookings_colLocation"),
        enableEditing: row =>
          isEditable && isVisitorAdminScheduleFieldEditable(row.original, "location"),
        accessorFn: row => visitorBookingLocationDisplay(row),
        Edit: ({ row }) => renderVisitorBookingsLocationEdit({ row, locations }),
        Cell: ({ row }) => visitorBookingLocationDisplay(row.original)
      },
      {
        id: "consent",
        header: t("_visitorBookings_colConsent"),
        enableEditing: false,
        size: 230,
        accessorFn: row => visitorBookingConsentColumnAccessorValue(row, t),
        Cell: ({ row }) => renderVisitorBookingsConsentCell({ row })
      },
      ...(mode === "archive"
        ? []
        : [
            {
              id: "consentLink",
              header: t("_visitorBookings_colConsentLink"),
              enableEditing: false,
              accessorFn: (row: VisitorBookingTableRow) => row.consentStatus ?? "",
              Cell: ({ row }: { row: MRT_Row<VisitorBookingTableRow> }) =>
                renderVisitorBookingsConsentLinkCell({
                  row,
                  timezone,
                  dateFormat,
                  consentLinkSentRowKeys,
                  canManageConsentLink,
                  sendingRowKey: resendConsentLink.isLoading ? sendingConsentLinkRowKey : null,
                  onSendLink: handleSendConsentLink
                })
            }
          ]),
      {
        id: "place",
        header: t("_visitorBookings_colPlaceOrRoom"),
        enableEditing: false,
        Cell: ({ row }) =>
          renderVisitorBookingsPlaceCell({
            row: row as unknown as MRT_Row<VisitorBookingTableRow>,
            showPlaceLabel: t("Show place"),
            showZoneLabel: t("Show Zone"),
            onShowPlace: (bookingId, bookingType, highlight) =>
              setShowPlaceDialogState({ bookingId, bookingType, highlight })
          })
      }
    ],
    [
      t,
      colleagues,
      onRefetch,
      dateFormat,
      localeFullFormat,
      isEditable,
      draftByRowId,
      validationErrors,
      timezone,
      maxDateForVisitor,
      clearValidationErrorForField,
      setDraftForRow,
      locations,
      canManageConsentLink,
      consentLinkSentRowKeys,
      handleSendConsentLink,
      resendConsentLink.isLoading,
      sendingConsentLinkRowKey,
      canManageRequestedAtLocation,
      locations
    ]
  );

  const handleRowDelete = async (selectedRows: VisitorBookingTableRow[]) => {
    const deletableRows = selectedRows.filter(row => canDeleteRow(row));
    if (!deletableRows.length) return;

    const { approvalRejectRows, visitorAdminRejectRows } = partitionRowsForVisitorReject(
      deletableRows,
      canManageRequestedForRow
    );
    const approvalPayload = buildBookingApprovalRejectPayload(approvalRejectRows, bulkRejectReason);
    const visitorItems = buildVisitorAdminBulkRejectItems(visitorAdminRejectRows);

    if (!approvalPayload && visitorItems.length === 0) {
      return;
    }

    const cancelSnackbarKeys = getVisitorBookingVisitActionSnackbarKeys("cancel");
    const trimmedReason = bulkRejectReason.trim();
    setDeleteStatus("loading");
    try {
      if (approvalPayload) {
        await patchBookingRequestStatus(approvalPayload);
      }
      if (visitorItems.length > 0) {
        await postVisitorAdminBulkRejectWithFallback({
          ...(trimmedReason.length > 0 ? { reason: trimmedReason } : {}),
          items: visitorItems
        });
      }
      const deletedRowIds = new Set(deletableRows.map(row => row.rowId));
      setTableData(prev => prev.filter(item => !deletedRowIds.has(item.rowId)));
      setRowSelection({});
      setDeletionSelected(null);
      setBulkRejectReason("");
      setDeleteStatus("success");
      setIsDeleteModalOpen(false);
      enqueueSnackbar(t(cancelSnackbarKeys.success), { variant: "success" });
      onRefetch();
    } catch {
      setDeleteStatus("error");
      enqueueSnackbar(t(cancelSnackbarKeys.failed), { variant: "error" });
    }
  };
  const seeditMrtOptions = useSeeditMrtTableOptions<VisitorBookingTableRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns,
    data: tableData,
    getRowId: row => row.rowId,
    enableRowSelection: isEditable
      ? (row: MRT_Row<VisitorBookingTableRow>) => canDeleteRow(row.original)
      : false,
    enableRowActions: isEditable,
    enableHiding: true,
    enableEditing: isEditable,
    editDisplayMode: "modal",
    onEditingRowSave,
    onEditingRowCancel: ({ row }) => {
      if (row?.id) {
        clearDraftForRow(row.id);
        setValidationErrors(prev => {
          const next = { ...prev };
          delete next[row.id];
          return next;
        });
      }
    },
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: updater => {
      setColumnVisibility(prev => (typeof updater === "function" ? updater(prev) : updater));
    },
    renderTopToolbar: ({ table }) => (
      <ListTopToolbar
        title={t("Drawer_Visitors")}
        createBtnLabel={t("_visitorBookings_addBooking")}
        createButtonTestId="visitor-bookings-add-booking-btn"
        table={table as unknown as MRT_TableInstance<Record<string, unknown>>}
        isEditable={isEditable}
        hideCreateButton={false}
        onCreateClick={handleAddVisitorBooking}
        disabledDeleteButton={table.getSelectedRowModel().flatRows.length <= 0}
        onDelete={() => {
          setIsDeleteModalOpen(true);
          const selected = table
            .getSelectedRowModel()
            .rows.map(r => r.original)
            .filter(row => canDeleteRow(row));
          setDeletionSelected(selected);
        }}
        titleActions={
          <Tooltip title={<Typography>{t("Show/hide optional columns")}</Typography>}>
            <Button
              sx={{ height: "40px", minWidth: 30 }}
              color={showColumnTogglesPanel ? "primary" : "secondary"}
              onClick={() => setShowColumnTogglesPanel(prev => !prev)}
              data-testid="visitor-bookings-toggle-column-panel"
              aria-pressed={showColumnTogglesPanel}
            >
              <ViewColumn />
            </Button>
          </Tooltip>
        }
        childComponent={
          showColumnTogglesPanel ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 2,
                width: "100%"
              }}
            >
              <VisitorBookingsTableColumnToggles
                visible={{
                  visitorCount: columnVisibility.visitorCount,
                  externalCompany: columnVisibility.externalCompany,
                  externalEmail: columnVisibility.externalEmail,
                  externalContacts: columnVisibility.externalContacts,
                  externalVisitReason: columnVisibility.externalVisitReason,
                  location: columnVisibility.location
                }}
                onToggle={handleColumnToggle}
              />
            </Box>
          ) : undefined
        }
      />
    ),
    renderRowActions: ({ row, table }) =>
      isVisitorAdminTableRowEditable(row.original) ? (
        <ListRowActions
          title="visitor-bookings"
          row={row as unknown as MRT_Row<any>}
          table={table as unknown as MRT_TableInstance<any>}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          setDeletionSelected={() => undefined}
          onRequestDelete={() => {
            setDeletionSelected([row.original]);
          }}
          canDelete={canDeleteRow(row.original)}
        />
      ) : null,
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <VisitorBookingsSlidePanelEditRow
        internalEditComponents={internalEditComponents}
        headline={t("_visitorBookings_editSlideoutTitle")}
        table={table}
        row={row}
      />
    ),
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay:
        mode === "active"
          ? t("_visitorBookings_noBookings")
          : t("_visitorBookings_noBookingsForPeriod")
    },
    paginationDisplayMode: "pages",
    muiPaginationProps: { rowsPerPageOptions: [10, 20, 30], variant: "outlined" },
    initialState: withSeeditMrtInitialState({
      pagination: { pageSize: 20, pageIndex: 0 },
      showGlobalFilter: true,
      showColumnFilters: true,
      sorting: [{ id: "start", desc: mode !== "archive" }],
      columnOrder: visitorBookingsDefaultColumnOrder
    }),
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableColumnPinning: true,
    autoResetPageIndex: false,
    state: {
      isLoading,
      showProgressBars: isLoading,
      isSaving: deleteStatus === "loading" || patchVisitOnly.isLoading || patchConference.isLoading,
      columnVisibility,
      rowSelection: isEditable ? rowSelection : {}
    }
  });

  return (
    <>
      <Box data-testid="visitor-bookings-table-parent">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MaterialReactTable table={table} />
        </LocalizationProvider>
      </Box>

      <TableDeleteConfirmDialog
        onConfirm={() => {
          if (deletionSelected?.length) handleRowDelete(deletionSelected);
        }}
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        onDeleteModalExited={() => setBulkRejectReason("")}
        dialogDesc={
          deletionSelected?.length === 1
            ? t("_tableSingleDeletionConfirmationDescNeutral", {
                title: t("_deleteConfirmVisitorBooking")
              })
            : t("_tableDeletionConfirmationDesc")
        }
        deleteStatus={deleteStatus}
      >
        <TextField
          fullWidth
          multiline
          minRows={1}
          sx={{ mt: 1, mb: 2 }}
          label={t("_visitorBookings_rejectVisitReasonLabel")}
          placeholder={t("_visitorBookings_rejectVisitReasonPlaceholder")}
          value={bulkRejectReason}
          onChange={event => setBulkRejectReason(event.target.value)}
          slotProps={{
            htmlInput: { maxLength: 2000, "data-testid": "visitor-bulk-reject-reason" }
          }}
        />
      </TableDeleteConfirmDialog>
      {showPlaceDialogState ? (
        <BookingSvgDialog
          isShowDialogOpen
          bookingId={showPlaceDialogState.bookingId}
          bookingType={showPlaceDialogState.bookingType}
          highlight={showPlaceDialogState.highlight}
          closeFn={() => setShowPlaceDialogState(null)}
          labelButton={t("_bookingSvgDialog_closeButton")}
        />
      ) : null}

      <VisitorAdminNewBookingDialog
        booker={visitorAdminBooker}
        onBookingCompleted={() => onRefetch()}
        onClose={() => setVisitorAdminNewBookingOpen(false)}
        open={visitorAdminNewBookingOpen}
      />
    </>
  );
}
